import React, { useEffect, useState } from 'react';
import {
  FilterSweepRunsResponseSchema,
  type FilterSweepRun,
} from '../../schemas/apiResponse.schemas';
import './Filter.css';

interface FilteredOutEntry {
  orderId: string | number;
  orderNumber?: string | number | null;
  skus: string[];
}

interface MissingBundleDefaultEntry {
  orderId: string | number;
  orderNumber?: string | number | null;
  parentSku: string;
}

interface ShipstationFilterEvent {
  type: 'progress' | 'complete' | 'error';
  current: number;
  total: number;
  orderId?: string | number;
  orderNumber?: string | number | null;
  filteredOut?: FilteredOutEntry;
  missingBundleDefaults?: MissingBundleDefaultEntry[];
  error?: string;
  message?: string;
}

const SWEEP_STALE_AFTER_MS = 25 * 60 * 1000;

function formatOrderNumber(orderNumber: string | number | null | undefined, orderId: string | number | undefined): string {
  if (orderNumber != null && String(orderNumber).trim() !== '') {
    return String(orderNumber);
  }
  if (orderId != null) {
    return `#${orderId}`;
  }
  return '—';
}

function formatDurationMs(durationMs: number): string {
  if (durationMs < 1000) {
    return `${durationMs} ms`;
  }
  if (durationMs < 60_000) {
    return `${(durationMs / 1000).toFixed(1)}s`;
  }
  const minutes = Math.floor(durationMs / 60_000);
  const seconds = Math.round((durationMs % 60_000) / 1000);
  return `${minutes}m ${seconds}s`;
}

function formatRelativeFinish(finishedAt: string, nowMs: number): string {
  const finishedMs = Date.parse(finishedAt);
  if (Number.isNaN(finishedMs)) {
    return finishedAt;
  }
  const deltaMs = Math.max(0, nowMs - finishedMs);
  const seconds = Math.floor(deltaMs / 1000);
  if (seconds < 45) {
    return 'just now';
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 48) {
    return `${hours} hr ago`;
  }
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function formatSweepCounts(run: FilterSweepRun): string {
  return `processed ${run.processed}, skipped ${run.skipped}, failed ${run.failed}`;
}

function formatSweepMeta(run: FilterSweepRun): string {
  const environment = run.environment && run.environment.trim() !== '' ? run.environment : 'unknown';
  return `${environment} · ${run.trigger}`;
}

const Filter: React.FC = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [filteredOutLog, setFilteredOutLog] = useState<FilteredOutEntry[]>([]);
  const [missingDefaultsLog, setMissingDefaultsLog] = useState<MissingBundleDefaultEntry[]>([]);
  const [sweepRuns, setSweepRuns] = useState<FilterSweepRun[]>([]);
  const [sweepLoadError, setSweepLoadError] = useState<string | null>(null);
  const [sweepLoaded, setSweepLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadSweepRuns = async (): Promise<void> => {
      try {
        const response = await fetch('/api/orders/filter/sweep-runs');
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const json: unknown = await response.json();
        const parsed = FilterSweepRunsResponseSchema.parse(json);
        if (!cancelled) {
          setSweepRuns(parsed.runs);
          setSweepLoadError(null);
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setSweepLoadError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled) {
          setSweepLoaded(true);
        }
      }
    };

    void loadSweepRuns();
    return () => {
      cancelled = true;
    };
  }, []);

  const startProcessing = async (): Promise<void> => {
    try {
      setIsProcessing(true);
      setProgress(0);
      setStatus('Starting order processing...');
      setError(null);
      setFilteredOutLog([]);
      setMissingDefaultsLog([]);

      const response = await fetch('/api/shipstation/filter-orders', {
        method: 'POST',
        headers: {
          'Accept': 'text/event-stream',
          'Content-Type': 'application/json'
        }
      });

      if (!response.body) {
        throw new Error('No response body');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        const events = chunk.split('\n\n').filter(Boolean);

        for (const event of events) {
          if (event.startsWith('data: ')) {
            try {
              const jsonData: ShipstationFilterEvent = JSON.parse(event.substring(6));

              switch (jsonData.type) {
                case 'progress': {
                  const percentage = jsonData.total > 0
                    ? (jsonData.current / jsonData.total) * 100
                    : 0;
                  setProgress(percentage);
                  const orderLabel = formatOrderNumber(jsonData.orderNumber, jsonData.orderId);
                  if (jsonData.current === 0) {
                    setStatus(`Starting: ${jsonData.total} orders`);
                  } else {
                    setStatus(`Processing ${jsonData.current} of ${jsonData.total}: order ${orderLabel}`);
                  }
                  if (jsonData.filteredOut?.orderId != null && jsonData.filteredOut?.skus?.length) {
                    setFilteredOutLog((prev) => [...prev, jsonData.filteredOut as FilteredOutEntry]);
                  }
                  if (jsonData.missingBundleDefaults?.length) {
                    const missing = jsonData.missingBundleDefaults;
                    setMissingDefaultsLog((prev) => [...prev, ...missing]);
                  }
                  break;
                }

                case 'complete':
                  setProgress(100);
                  setStatus('Order processing complete!');
                  setIsProcessing(false);
                  break;

                case 'error':
                  setError(jsonData.message ?? 'Unknown error');
                  setStatus('Error occurred');
                  setIsProcessing(false);
                  break;

                default:
                  console.log('Unknown event type:', jsonData);
              }
            } catch (err) {
              console.error('Error parsing SSE data:', err, event);
            }
          }
        }
      }
    } catch (err) {
      console.error('Error in filter process:', err);
      setError(`Connection error: ${err instanceof Error ? err.message : String(err)}`);
      setStatus('Failed to connect');
      setIsProcessing(false);
    }
  };

  const lastRun = sweepRuns[0];
  const nowMs = Date.now();
  const lastRunIsStale = lastRun != null
    && !Number.isNaN(Date.parse(lastRun.finishedAt))
    && nowMs - Date.parse(lastRun.finishedAt) > SWEEP_STALE_AFTER_MS;

  return (
    <div className="filter-container">
      <h2>ShipStation Order Filtering</h2>

      <section className="sweep-runs" aria-label="Filter sweep cron runs">
        <h3>Cron sweep</h3>
        {!sweepLoaded && (
          <p className="sweep-runs-status">Loading sweep times…</p>
        )}
        {sweepLoaded && sweepLoadError && (
          <p className="sweep-runs-status sweep-runs-error">Could not load sweep times: {sweepLoadError}</p>
        )}
        {sweepLoaded && !sweepLoadError && sweepRuns.length === 0 && (
          <p className="sweep-runs-status">
            No sweep runs logged yet. Times appear after the next GitHub Action
            (every 10 minutes) or a local sweep.
          </p>
        )}
        {lastRun && (
          <div className="sweep-last-run">
            <p>
              Last finished {formatRelativeFinish(lastRun.finishedAt, nowMs)}
              {' '}({formatDurationMs(lastRun.durationMs)})
              {' — '}
              {formatSweepCounts(lastRun)}
            </p>
            <p className="sweep-run-meta">
              {lastRun.status}
              {' · '}
              {formatSweepMeta(lastRun)}
            </p>
            {lastRun.errorMessage && (
              <p className="sweep-run-error">{lastRun.errorMessage}</p>
            )}
            {lastRunIsStale && (
              <p className="sweep-stale-warning">
                Last finish is older than 25 minutes; the GitHub cron may be delayed.
              </p>
            )}
          </div>
        )}
        {sweepRuns.length > 1 && (
          <ul className="sweep-runs-list">
            {sweepRuns.slice(1).map((run) => (
              <li key={run.id}>
                {formatRelativeFinish(run.finishedAt, nowMs)}
                {' · '}
                {formatDurationMs(run.durationMs)}
                {' · '}
                {run.status}
                {' · '}
                {formatSweepCounts(run)}
                {' · '}
                {formatSweepMeta(run)}
              </li>
            ))}
          </ul>
        )}
      </section>

      {!isProcessing && !error && progress === 0 && (
        <div className="status-message">
          Click the button below to filter orders
        </div>
      )}

      <button
        className="filter-button"
        onClick={startProcessing}
        disabled={isProcessing}
      >
        {isProcessing ? 'Processing...' : 'Filter Orders'}
      </button>

      {(isProcessing || progress > 0) && (
        <div className="progress-container">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="progress-text">
            {status}
          </div>
        </div>
      )}

      {filteredOutLog.length > 0 && (
        <div className="filtered-out-readout">
          <h3>Filtered-out items</h3>
          <ul className="filtered-out-list">
            {filteredOutLog.map((entry, idx) => (
              <li key={`${entry.orderId}-${idx}`}>
                Order {formatOrderNumber(entry.orderNumber, entry.orderId)}: {entry.skus.join(', ')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {missingDefaultsLog.length > 0 && (
        <div className="filtered-out-readout">
          <h3>Empty bundles with no default list</h3>
          <ul className="filtered-out-list">
            {missingDefaultsLog.map((entry, idx) => (
              <li key={`${entry.orderId}-${entry.parentSku}-${idx}`}>
                Order {formatOrderNumber(entry.orderNumber, entry.orderId)}: {entry.parentSku}
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <div className="error-message">
          Error: {error}
        </div>
      )}
    </div>
  );
};

export default Filter;
